import React, { useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from '../hooks/useTranslation';
import { categorieAPI } from '../config/api';
import './Home.css';

// Importa le immagini per l'hero
import header1 from '../images/header1.jpg';
import header2 from '../images/header2.jpg';
import header3 from '../images/header3.jpg';
import header4 from '../images/header4.jpg';

const Home = () => {
  const { t } = useTranslation();
  const [prodotti, setProdotti] = useState([]);
  const [prodottiFiltrati, setProdottiFiltrati] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtri
  const [filtri, setFiltri] = useState({
    categorie: [], // Array di ID categorie selezionate
    prezzoMin: '',
    prezzoMax: '',
    disponibilita: 'tutti' // 'tutti', 'disponibili', 'esauriti'
  });
  
  // Ordinamento
  const [ordinamento, setOrdinamento] = useState('data-desc'); // 'nome-asc', 'nome-desc', 'prezzo-asc', 'prezzo-desc', 'data-asc', 'data-desc'
  
  // Sidebar filtri
  const [sidebarAperta, setSidebarAperta] = useState(false);
  
  // Stato per l'hero slider
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const heroImages = [header1, header2, header3, header4];

  useEffect(() => {
    loadData();
  }, []);

  // Effetto per il cambio automatico delle immagini hero con fade
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
        );
        setIsTransitioning(false);
      }, 300); // Metà della transizione per il fade out
      
    }, 3000); // Cambia ogni 3 secondi

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const loadData = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? '/shop/backend/api' 
        : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

      // 1) Carica categorie
      const catRes = await fetch(`${API_BASE_URL}/categorie.php`);
      const catJson = await catRes.json();
      const categorieData = catJson.success ? (catJson.data || []) : [];
      setCategorie(categorieData);

      // 2) Carica SOLO prodotti attivi
      const prodRes = await fetch(`${API_BASE_URL}/prodotti.php?attivo=1`);
      const prodJson = await prodRes.json();

      let prodottiData = prodJson.success ? (prodJson.data || []) : [];

      // 3) Per ogni prodotto, recupera immagine principale e mappa nei campi usati dalla home
      prodottiData = await Promise.all(
        prodottiData.map(async (p) => {
          let fotoUrl = null;
          if (p.foto) {
            try {
              const imgRes = await fetch(`${API_BASE_URL}/get_images.php?folder=${p.foto}`);
              const imgJson = await imgRes.json();
              if (imgJson.success && imgJson.data.main_image) {
                fotoUrl = imgJson.data.main_image.full_url;
              }
            } catch (e) {
              console.warn('Errore caricamento immagine home:', e);
            }
          }

          return {
            id: p.id_prodotto,
            nome: p.titolo,
            prezzo: p.prezzo,
            foto: fotoUrl,
          quantita: Number(p.quantita ?? 0),
            categorie: p.categoria_nome || undefined,
            id_categoria: p.id_categoria,
            data_creazione: p.data_creazione || new Date().toISOString()
          };
        })
      );

      setProdotti(prodottiData);
      setProdottiFiltrati(prodottiData);
      setError(null);
    } catch (err) {
      setError('Errore nel caricamento dei prodotti. Riprova più tardi.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per gestire i filtri
  const applicaFiltri = () => {
    let prodottiFiltrati = [...prodotti];

    // Filtro per categorie
    if (filtri.categorie.length > 0) {
      prodottiFiltrati = prodottiFiltrati.filter(prodotto => 
        filtri.categorie.includes(parseInt(prodotto.id_categoria))
      );
    }

    // Filtro per prezzo
    if (filtri.prezzoMin !== '') {
      prodottiFiltrati = prodottiFiltrati.filter(prodotto => 
        parseFloat(prodotto.prezzo) >= parseFloat(filtri.prezzoMin)
      );
    }
    if (filtri.prezzoMax !== '') {
      prodottiFiltrati = prodottiFiltrati.filter(prodotto => 
        parseFloat(prodotto.prezzo) <= parseFloat(filtri.prezzoMax)
      );
    }

    // Filtro disponibilità
    if (filtri.disponibilita === 'disponibili') {
      prodottiFiltrati = prodottiFiltrati.filter(prodotto => (prodotto.quantita ?? 0) > 0);
    } else if (filtri.disponibilita === 'esauriti') {
      prodottiFiltrati = prodottiFiltrati.filter(prodotto => (prodotto.quantita ?? 0) <= 0);
    }

    // Ordinamento
    prodottiFiltrati.sort((a, b) => {
      switch (ordinamento) {
        case 'nome-asc':
          return a.nome.localeCompare(b.nome);
        case 'nome-desc':
          return b.nome.localeCompare(a.nome);
        case 'prezzo-asc':
          return parseFloat(a.prezzo) - parseFloat(b.prezzo);
        case 'prezzo-desc':
          return parseFloat(b.prezzo) - parseFloat(a.prezzo);
        default:
          return 0;
      }
    });

    setProdottiFiltrati(prodottiFiltrati);
  };

  // Effetto per applicare filtri quando cambiano
  useEffect(() => {
    if (prodotti.length > 0) {
      applicaFiltri();
    }
  }, [prodotti, filtri, ordinamento]);

  // Funzioni per gestire i filtri
  const toggleCategoria = (categoriaId) => {
    const categoriaIdNum = parseInt(categoriaId);
    setFiltri(prev => ({
      ...prev,
      categorie: prev.categorie.includes(categoriaIdNum)
        ? prev.categorie.filter(id => id !== categoriaIdNum)
        : [...prev.categorie, categoriaIdNum]
    }));
  };

  const resetFiltri = () => {
    setFiltri({
      categorie: [],
      prezzoMin: '',
      prezzoMax: '',
      disponibilita: 'tutti'
    });
    setOrdinamento('data-desc');
  };

  // Funzioni per gestire la sidebar
  const apriSidebar = () => {
    setSidebarAperta(true);
  };

  const chiudiSidebar = () => {
    setSidebarAperta(false);
  };


  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div 
          className={`hero-background ${isTransitioning ? 'fade-out' : 'fade-in'}`}
          style={{
            backgroundImage: `url(${heroImages[currentImageIndex]})`
          }}
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('home.title')}</h1>
          <p className="hero-subtitle">
            {t('home.subtitle')}
          </p>
        </div>
      </section>

      <div className="container">
        {/* Controlli Filtri e Ordinamento */}
        <section className="controls-section">
          <div className="controls-header">
            <button className="filters-toggle-btn" onClick={apriSidebar}>
              <i className="fas fa-filter"></i>
              {t('home.filters.filters')}
            </button>
            
            <div className="sorting-controls">
              <select 
                value={ordinamento} 
                onChange={(e) => setOrdinamento(e.target.value)}
                className="sort-select"
              >
                <option value="data-desc">{t('home.sort.most_recent')}</option>
                <option value="data-asc">Meno recenti</option>
                <option value="nome-asc">{t('home.sort.name_az')}</option>
                <option value="nome-desc">{t('home.sort.name_za')}</option>
                <option value="prezzo-asc">{t('home.sort.price_low')}</option>
                <option value="prezzo-desc">{t('home.sort.price_high')}</option>
              </select>
              
              <div className="results-info">
                <span>{prodottiFiltrati.length} {t('home.products_found')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Sidebar Filtri */}
        <div className={`sidebar-overlay ${sidebarAperta ? 'active' : ''}`} onClick={chiudiSidebar}></div>
        
        <div className={`filters-sidebar ${sidebarAperta ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h3>{t('home.filters.filters')}</h3>
            <button className="close-sidebar-btn" onClick={chiudiSidebar}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="sidebar-content">
            {/* Filtro Categorie */}
            {categorie.length > 0 && (
              <div className="filter-group">
                <h4>{t('home.filters.category_label')}</h4>
                <div className="filter-checkboxes">
                  {categorie.map(categoria => {
                    const categoriaId = categoria.id || categoria.id_categoria || categoria.ID;
                    return (
                      <label key={categoriaId} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={filtri.categorie.includes(parseInt(categoriaId))}
                          onChange={() => toggleCategoria(categoriaId)}
                        />
                        <span className="checkmark"></span>
                        {categoria.nome} ({categoria.numero_prodotti})
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filtro Prezzo */}
            <div className="filter-group">
              <h4>{t('home.filters.price')}</h4>
              <div className="price-range">
                <input
                  type="number"
                  placeholder={`${t('home.filters.from')} €`}
                  value={filtri.prezzoMin}
                  onChange={(e) => setFiltri(prev => ({...prev, prezzoMin: e.target.value}))}
                  min="0"
                  step="0.01"
                />
                <span>—</span>
                <input
                  type="number"
                  placeholder={`${t('home.filters.to')} €`}
                  value={filtri.prezzoMax}
                  onChange={(e) => setFiltri(prev => ({...prev, prezzoMax: e.target.value}))}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Filtro Disponibilità */}
            <div className="filter-group">
              <h4>{t('home.filters.availability')}</h4>
              <select
                value={filtri.disponibilita}
                onChange={(e) => setFiltri(prev => ({ ...prev, disponibilita: e.target.value }))}
                className="availability-select"
              >
                <option value="tutti">{t('home.filters.availability_all')}</option>
                <option value="disponibili">{t('home.filters.availability_in_stock')}</option>
                <option value="esauriti">{t('home.filters.availability_out_of_stock')}</option>
              </select>
            </div>

            {/* Reset Filtri */}
            <button className="reset-filters-btn" onClick={resetFiltri}>
              {t('home.filters.reset')}
            </button>
          </div>
        </div>

        {/* Prodotti */}
        <section className="products-section">
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="error">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={loadData}>
                {t('home.retry')}
              </button>
            </div>
          ) : prodottiFiltrati.length === 0 ? (
            <div className="empty">
              <p>{t('home.empty')}</p>
              <button className="btn btn-primary" onClick={resetFiltri}>
                {t('home.filters.reset')}
              </button>
            </div>
          ) : (
            <div className="products-grid">
              {prodottiFiltrati.map(prodotto => (
                <ProductCard key={prodotto.id} prodotto={prodotto} />
              ))}
            </div>
          )}
        </section>
      </div>
      
      {/* Language Selector */}
      <LanguageSelector />
    </div>
  );
};

export default Home;

